import { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import authAPI from "../../api/authAPI";
import styles from "./PasswordReset.module.css";

const WelcomeForm = () => {
  const { t } = useTranslation("common");
  const { token } = useParams();
  const history = useHistory();
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState("weak");

  useEffect(() => {
    document.body.classList.add(styles.background);
    return () => document.body.classList.remove(styles.background);
  }, []);

  useEffect(() => {
    if (!token) {
      history.push("/");
    }
  }, [token, history]);

  useEffect(() => {
    calculatePasswordStrength(password);
  }, [password]);

  function calculatePasswordStrength(pwd) {
    if (pwd.length === 0) {
      setPasswordStrength("weak");
      return;
    }

    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (pwd.length >= 12) strength++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength++;
    if (/\d/.test(pwd)) strength++;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength++;

    if (strength <= 2) setPasswordStrength("weak");
    else if (strength <= 4) setPasswordStrength("medium");
    else setPasswordStrength("strong");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError(t("auth.welcome.nameRequired"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("auth.resetPassword.passwordMismatch"));
      return;
    }

    if (password.length < 8) {
      setError(t("auth.resetPassword.passwordTooShort"));
      return;
    }

    setIsSubmitting(true);

    try {
      await authAPI.confirmWelcome(token, fullName, password);
      history.push("/");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          t("auth.welcome.setupFailed")
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  const getStrengthText = () => {
    switch (passwordStrength) {
      case "weak":
        return t("auth.resetPassword.weak");
      case "medium":
        return t("auth.resetPassword.medium");
      case "strong":
        return t("auth.resetPassword.strong");
      default:
        return "";
    }
  };

  return (
    <main className={styles.container}>
      <h1 className={styles.title}>{t("auth.welcome.title")}</h1>

      <form className={styles.form} onSubmit={handleSubmit}>
        <p className={styles.description}>
          {t("auth.welcome.description")}
        </p>

        <div className={styles.inputGroup}>
          <label htmlFor="fullName">{t("auth.welcome.fullName")}</label>
          <input
            type="text"
            id="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            disabled={isSubmitting}
            className={styles.input}
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="password">{t("auth.welcome.password")}</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
            className={styles.input}
            minLength={8}
          />
          {password && (
            <div className={styles.strengthIndicator}>
              <div>
                {t("auth.resetPassword.passwordStrength")}: <strong>{getStrengthText()}</strong>
              </div>
              <div className={styles.strengthBar}>
                <div className={`${styles.strengthFill} ${styles[passwordStrength]}`} />
              </div>
            </div>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="confirmPassword">{t("auth.welcome.confirmPassword")}</label>
          <input
            type="password"
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isSubmitting}
            className={styles.input}
            minLength={8}
          />
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <button
          type="submit"
          className={styles.button}
          disabled={isSubmitting || !fullName || !password || !confirmPassword}
        >
          {isSubmitting ? t("auth.welcome.submitting") : t("auth.welcome.submitButton")}
        </button>

        <a href="/" className={styles.backLink}>
          {t("auth.resetPassword.backToLogin")}
        </a>
      </form>
    </main>
  );
};

export default WelcomeForm;
